/**
 * La géométrie du dessin de la carte : du calcul, et pas une ligne de JSX.
 *
 * Les places des bulles viennent du serveur (`domain/carte/carte.ts`), centrées sur le titre de la
 * feuille. Ici, seulement ce qui relève du trait : une courbe qui se creuse du mauvais côté
 * se lit comme un dessin maladroit, jamais comme un bug, et un fichier sans React se relit.
 *
 * Elle ne stocke rien : **aucune position n'est jamais enregistrée** (interdit n°2).
 */

export interface Point {
  x: number;
  y: number;
}

/**
 * Un lien n'est pas un segment : la planche courbe chaque trait. Deux points de contrôle
 * posés le long du chemin et décalés du même côté donnent l'arc doux du dessin — des
 * segments droits font une roue de vélo, la planche fait un nuage.
 */
export function courbe(depart: Point, arrivee: Point): string {
  const [c1, c2] = controles(depart, arrivee);
  const n = (v: number) => Math.round(v * 10) / 10;
  return `M${n(depart.x)} ${n(depart.y)} C ${n(c1.x)} ${n(c1.y)}, ${n(c2.x)} ${n(c2.y)}, ${n(arrivee.x)} ${n(arrivee.y)}`;
}

/** Les deux points de contrôle de la courbe : le long du chemin, décalés du même côté. */
function controles(depart: Point, arrivee: Point): [Point, Point] {
  const dx = arrivee.x - depart.x;
  const dy = arrivee.y - depart.y;
  const bombe = 0.11;
  return [
    { x: depart.x + dx * 0.32 - dy * bombe, y: depart.y + dy * 0.32 + dx * bombe },
    { x: depart.x + dx * 0.72 - dy * bombe, y: depart.y + dy * 0.72 + dx * bombe },
  ];
}

/**
 * Le milieu de la courbe, et son sens à cet endroit (l'angle de la tangente, en radians) : la
 * flèche d'un lien s'y pose (chantier liens-entre-bulles, ticket 03), et les signes d'un trait
 * cliqué aussi. Pas à son bout : un trait s'arrête au bord de la bulle, sous le masque.
 */
export function milieuDeLaCourbe(depart: Point, arrivee: Point): { point: Point; angle: number } {
  const [c1, c2] = controles(depart, arrivee);
  // Une courbe de Bézier cubique, à t = 1/2, et sa dérivée.
  const point = {
    x: (depart.x + 3 * c1.x + 3 * c2.x + arrivee.x) / 8,
    y: (depart.y + 3 * c1.y + 3 * c2.y + arrivee.y) / 8,
  };
  const tx = arrivee.x + c2.x - c1.x - depart.x;
  const ty = arrivee.y + c2.y - c1.y - depart.y;
  return { point, angle: Math.atan2(ty, tx) };
}

/** Une petite pointe de flèche posée en `point`, tournée selon `angle` : un chevron au trait. */
export function pointeDeFleche({ point, angle }: { point: Point; angle: number }, taille = 6): string {
  const n = (v: number) => Math.round(v * 10) / 10;
  const bout = { x: point.x + Math.cos(angle) * taille * 0.6, y: point.y + Math.sin(angle) * taille * 0.6 };
  const aile = (signe: number) => ({
    x: bout.x - Math.cos(angle + signe * 0.55) * taille,
    y: bout.y - Math.sin(angle + signe * 0.55) * taille,
  });
  const [g, d] = [aile(1), aile(-1)];
  return `M${n(g.x)} ${n(g.y)} L${n(bout.x)} ${n(bout.y)} L${n(d.x)} ${n(d.y)}`;
}

/** Sur l'ellipse (a, b) agrandie de `part`, à cet angle : là où se posent halos et noms. */
export function surLEllipse(anneau: { a: number; b: number }, angle: number, part = 1): Point {
  return { x: anneau.a * part * Math.cos(angle), y: anneau.b * part * Math.sin(angle) };
}
